// Component specific for H2k file upload in Create a Model. TODO: Create general file drag and drop
import React, { useRef, useEffect, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { importH2kFile } from "utils/h2kinout/api";
import { imageRead } from "features/Model/ImageGallery/utils/functions";
import { selectUserUid } from "store/users/selectors";

import UploadFile from "components/UploadFile";

import classes from "./style.module.scss";
import UploadIcon from "assets/images/icons/uploadSvg.svg";

const FileDragAndDropUpload = ({
    name,
    id,
    text,
    handleUpload,
    accept: allowedFileTypesStr = "",
    parentFolderId = "",
    uid,
}) => {
    const ref = useRef(null);

    const [importState, updateState] = useState("");
    const [isOverInput, setIsOverInput] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const allowedFileTypes = allowedFileTypesStr.split(", ");

    useEffect(() => {
        if (!ref.current) return;

        ref.current.addEventListener("dragenter", onDragEnter);
        ref.current.addEventListener("dragleave", onDragLeave);

        return () => {
            if (!ref.current) return;

            ref.current.removeEventListener("dragenter", onDragEnter);
            ref.current.removeEventListener("dragleave", onDragLeave);
        };
    }, []);

    const onDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.target !== ref.current) {
            setIsOverInput(true);
            setErrorMessage(null);
        }
    };

    const onDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.target !== ref.current) {
            setIsOverInput(false);
        }
    };

    const handleInputChange = async (e, isDropdown = false) => {
        e.preventDefault();
        e.stopPropagation();

        if (isUploading) return;

        setErrorMessage(null);
        setIsUploading(true);
        document.body.style.cursor = "progress";

        updateState("importing");

        const formData = new FormData();
        const fileName = !isDropdown ? e?.target?.files[0]?.name : e?.dataTransfer?.files[0]?.name || "";

        // Check if files are dropped or selected
        const filesToUpload = Object.values(!isDropdown ? e.target.files : e.dataTransfer.files);

        // Display file name in the input if dragged and dropped
        if (isDropdown) {
            document.getElementById(`${id}-drag-and-drop`).files = e.dataTransfer.files;
        }

        if (!filesToUpload || filesToUpload.length === 0) {
            setIsUploading(false);
            document.body.style.cursor = "default";

            return;
        }

        // Check if the file type is allowed
        if (
            filesToUpload &&
            filesToUpload.some((file) => {
                const splittedName = file.name.split(".");
                return !allowedFileTypes.includes(`.${file.type || splittedName.at(-1)}`.toLowerCase());
            })
        ) {
            setErrorMessage("Invalid file type");
            setIsUploading(false);
            updateState("failed");
            document.body.style.cursor = "default";

            handleUpload({});
            setTimeout(() => setErrorMessage(null), 3000);

            document.getElementById(`${id}-drag-and-drop`).value = uploadedFiles[0]?.name || null;
            return;
        }

        const updatedFiles = await Promise.all(
            filesToUpload.map((file) =>
                imageRead(file).then(() => {
                    const splittedName = file.name.split(".");

                    return {
                        id: uuidv4(),
                        name: file.name,
                        type: file.type ? file.type.split("/")[0] : "", // image in image/png, application in application/pdf, etc.
                        fileType: file.type ? file.type : splittedName.at(-1),
                        // isUploading: true,
                        progress: 100,
                        objectUrl: URL.createObjectURL(file),
                        fileToUpload: file,
                        size: file.size,
                        metadata: !isEmpty(file.exifdata)
                            ? {
                                  dateTimeOriginal: file.exifdata.DateTimeOriginal || null,
                                  orientation: file.exifdata.Orientation || null,
                              }
                            : null,
                    };
                })
            )
        );

        setUploadedFiles(updatedFiles);

        formData.append("modelFile", filesToUpload[0]);
        formData.append(
            "name",
            JSON.stringify({
                name: fileName?.split(".")?.[0] || "",
                notes: "Insert notes for imported model here",
                templateID: "H2kImport",
                templateName: "H2kImport",
                parentFolderId,
            })
        );

        importH2kFile(formData)
            .then((data) => {
                handleUpload(data.data);
                updateState("success");
                setIsUploading(false);

                document.body.style.cursor = "default";
            })
            .catch((err) => {
                handleUpload({});
                updateState("failed");
                setIsUploading(false);

                document.body.style.cursor = "default";
            });
    };

    return (
        <div>
            <div className={classes.inputWrapper}>
                <div className={classes.text} style={{ marginBottom: "10px" }}>
                    <p>{text}</p>
                </div>
            </div>
            <div
                className={classes.dragAndDropFieldContainer}
                style={{
                    pointerEvents: isUploading ? "none" : "auto",
                    cursor: isUploading ? "progress" : "auto",
                }}
            >
                <div
                    ref={ref}
                    className={`${classes.dragAndDropFieldWrapper}`}
                    style={{
                        border: `1px ${isOverInput ? "solid" : "dashed"} ${errorMessage ? "#c21818" : "#62bcf8"}`,
                        background: errorMessage ? "rgba(194, 24, 24, 0.1)" : "#fff",
                    }}
                    onDrop={(event) => handleInputChange(event, true)}
                >
                    <input
                        id={`${id}-drag-and-drop`}
                        className={`${classes.dragAndDropInput} ${isUploading ? classes.uploadingFiles : ""}`}
                        type={"file"}
                        name={"files"} //
                        accept={allowedFileTypesStr}
                        onClick={(event) => {
                            event.target.value = null;
                        }}
                        onChange={(event) => handleInputChange(event, false)}
                        disabled={isUploading}
                    />
                    {errorMessage ? (
                        <div className={`${classes.dragAndDropTextWrapper}`}>
                            <p className={classes.mainText} style={{ color: errorMessage ? "#c61717" : "#3B3B3B" }}>
                                {`${errorMessage}, only ${allowedFileTypesStr}`}
                            </p>
                        </div>
                    ) : (
                        <div className={`${classes.dragAndDropTextWrapper}`}>
                            <img src={UploadIcon} alt="Upload icon" />
                            <p className={classes.mainText}>
                                Drag and drop or <span className={classes.blueText}>browse files</span>
                            </p>
                        </div>
                    )}
                    {!errorMessage && (
                        <span className={`${classes.dragAndDropAllowedCategories}`}>({allowedFileTypesStr})</span>
                    )}
                </div>
                {importState === "importing" && (
                    <div className={classes.loading}>
                        <p>Processing...</p>
                    </div>
                )}
                {["success", "failed"].includes(importState) && uploadedFiles.length > 0 && (
                    <div className={classes.dragAndDropUploadFiles}>
                        {uploadedFiles.map(
                            ({ id, name, type, isUploading, progress, fileType, objectUrl, fileUrl }, index) => (
                                <UploadFile
                                    key={index}
                                    fileName={name}
                                    progress={progress}
                                    fileType={fileType}
                                    isUploading={isUploading}
                                    type={type}
                                    objectUrl={objectUrl}
                                    fileUrl={fileUrl}
                                    checkListFiles={uploadedFiles}
                                />
                            )
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    uid: selectUserUid,
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(FileDragAndDropUpload);
